import { camelizeKeys } from 'humps';
import { useEffect, useRef, useState } from 'react';
import useReactWebSocket from 'react-use-websocket';
import { getCustomHeaders } from '../utils/request';
import { logError, logWarning } from '../utils/remoteLogger';

interface IWebSocketOptions {
  // Connection options.
  appendCustomHeaders?: boolean;
  shareConnection?: boolean;
  onDisconnect?: () => void;

  // Content options.
  noCamelization?: boolean;
}

// As opposed to the fetch API, the websocket API needs an absolute url.
// Construct one from the current origin. CloudFront proxies /api/* to the
// ALB on every market (see terraform/modules/cf-static-site), including
// WebSocket upgrade; there is no reason to bypass it.
const getAbsoluteWebSocketUrl = (
  path: string,
  appendCustomHeaders?: boolean
): string => {
  const url = new URL(`${document.location.origin}${path}`);

  // Change the protocol of the url from https to wss.
  url.protocol = 'wss';

  // Append custom http headers as a query string to the url.
  if (appendCustomHeaders) {
    url.searchParams.append(
      'custom-headers',
      JSON.stringify(getCustomHeaders())
    );
  }

  return url.href;
};

// WebSocket reconnection configuration.
const RECONNECT_ATTEMPTS = 10;
const RECONNECT_INTERVAL = 3000;

// Multiple instances of the hook can exist simultaneously.
// This stores the timestamp of the last heartbeat for a given socket url,
// preventing other instances to send unnecessary heartbeats.
const previousHeartbeats: Record<string, number> = {};

// Store the timestamp of the current error, as to avoid having multiple
// hook instances report the same error.
let previousErrorTimestamp = 0;

// Store the timestamp of the last onError log to avoid excessive logging
// during normal reconnection attempts. We use a cooldown period to deduplicate.
const ERROR_LOG_COOLDOWN_MS = 30000; // 30 seconds
let lastErrorLogTimestamp = 0;

const useWebSocket = <T>(
  url?: string,
  options?: IWebSocketOptions
): T | undefined => {
  const [message, setMessage] = useState<T>();

  // Set to true once the hook unmounts, used in order to to not attempt
  // reconnects if the client purposefully closed the connection.
  const didUnmountRef = useRef(false);

  // Stores the heartbeat interval.
  const heartbeatIntervalRef = useRef<number>();

  // When we receive a new message, parse it, optionally camelize the json
  // and finally set it to state.
  const handleOnMessage = (event: WebSocketEventMap['message']) => {
    if (event.data) {
      const messageDataJson = JSON.parse(event.data);

      setMessage(
        options?.noCamelization
          ? messageDataJson
          : camelizeKeys(messageDataJson)
      );
    }
  };

  // Returns whether or not the hook should attempt to reconnect to the socket url
  // upon a disconnect.
  const handleShouldReconnect = () => {
    return !didUnmountRef.current;
  };

  // Helper function to build error details for logging.
  const buildErrorDetails = (additionalData?: Record<string, string>) => {
    const absoluteUrl = url
      ? getAbsoluteWebSocketUrl(url, options?.appendCustomHeaders)
      : null;

    return {
      url: url || 'undefined',
      absoluteUrl: absoluteUrl || 'null',
      online: String(navigator.onLine),
      timestamp: String(Date.now()),
      userAgent: navigator.userAgent,
      shareConnection: String(options?.shareConnection),
      appendCustomHeaders: String(options?.appendCustomHeaders),
      ...additionalData,
    };
  };

  // Reports potentially failed connections.
  const handleOnReconnectStop = () => {
    if (Date.now() > previousErrorTimestamp) {
      logError(
        'useWebSocket',
        `Web App connection stopped after ${RECONNECT_ATTEMPTS} attempts`,
        buildErrorDetails()
      );
      previousErrorTimestamp = Date.now();
    }
  };

  // Instantiate useReactWebSocket.
  const { sendMessage, readyState } = useReactWebSocket(
    url ? getAbsoluteWebSocketUrl(url, options?.appendCustomHeaders) : null,
    {
      share: options?.shareConnection,
      reconnectAttempts: RECONNECT_ATTEMPTS,
      reconnectInterval: RECONNECT_INTERVAL,

      onMessage: handleOnMessage,
      shouldReconnect: handleShouldReconnect,
      onReconnectStop: handleOnReconnectStop,
      onClose: (event: WebSocketEventMap['close']) => {
        options?.onDisconnect?.();

        // Only log abnormal closures (not normal disconnects or going away).
        // Avoid logging during reconnection attempts - let onReconnectStop handle final failure.
        // Close codes: 1000 = normal, 1001 = going away, 1006 = abnormal (no close frame).
        const isAbnormalClose = event.code !== 1000 && event.code !== 1001;
        const now = Date.now();
        const shouldLog =
          isAbnormalClose &&
          now - lastErrorLogTimestamp > ERROR_LOG_COOLDOWN_MS;

        if (shouldLog) {
          const ws = event.target as WebSocket | null;
          logWarning('useWebSocket', `Web App connection closed abnormally`, {
            ...buildErrorDetails({
              closeCode: String(event.code),
              closeReason: event.reason || 'No reason provided',
              wasClean: String(event.wasClean),
              readyState: String(ws?.readyState ?? -1),
            }),
          });
          lastErrorLogTimestamp = now;
        }
      },
    },
    !!url
  );

  // As we want the message to be momentary, remove it from the state as soon as it's been set.
  useEffect(() => {
    setMessage(undefined);
  }, [message]);

  // Sends a periodical heartbeat message through the websocket connection.
  useEffect(() => {
    if (readyState === 1) {
      heartbeatIntervalRef.current = window.setInterval(() => {
        if (url) {
          const lastHeartbeat = previousHeartbeats[url];
          const deltaFromNow = (Date.now() - lastHeartbeat) / 1000;

          // Send a heartbeat message if it hasn't already been sent within the last 10 seconds.
          if (!lastHeartbeat || deltaFromNow > 10) {
            // Send the heartbeat message and update the heartbeat history.
            sendMessage('');
            previousHeartbeats[url] = Date.now();
          }
        }
      }, 2000);
    }

    return () => {
      clearInterval(heartbeatIntervalRef.current);
    };
  }, [url, readyState, sendMessage]);

  // Update didUnmountRef which keeps track of whether or not the hook has unmounted.
  useEffect(() => {
    return () => {
      didUnmountRef.current = true;
    };
  }, []);

  return message;
};

export default useWebSocket;
